/**
 * Modal Controller (Stimulus)
 * - Backdrop click to close
 * - Close button click to close
 * - Escape key to close
 * - Turbo frame content clearing
 * - Body scroll lock with scroll position restore
 * - Browser back button handling
 * - Supports auth_modal and gun_modal frames
 */

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static savedScrollY = 0

  connect() {
    this.boundEscapeHandler = this.closeOnEscape.bind(this)
    this.boundPopstateHandler = this.handlePopstate.bind(this)

    document.addEventListener("keydown", this.boundEscapeHandler)
    window.addEventListener("popstate", this.boundPopstateHandler)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundEscapeHandler)
    window.removeEventListener("popstate", this.boundPopstateHandler)
    this.unlockBodyScroll()
  }

  // ==========================================================================
  // Scroll Lock
  // ==========================================================================

  lockBodyScroll() {
    if (document.body.dataset.scrollLocked === "true") return

    this.constructor.savedScrollY = window.scrollY

    document.body.dataset.scrollLocked = "true"
    document.body.style.overflow = "hidden"
    document.body.style.position = "fixed"
    document.body.style.top = `-${this.constructor.savedScrollY}px`
    document.body.style.left = "0"
    document.body.style.right = "0"
    document.body.style.width = "100%"
  }

  unlockBodyScroll() {
    if (document.body.dataset.scrollLocked !== "true") return

    const scrollY = this.constructor.savedScrollY

    document.body.dataset.scrollLocked = "false"
    document.body.style.overflow = ""
    document.body.style.position = ""
    document.body.style.top = ""
    document.body.style.left = ""
    document.body.style.right = ""
    document.body.style.width = ""

    window.scrollTo(0, scrollY)
  }

  isModalOpen() {
    const frame = this.element
    return frame && frame.innerHTML.trim() !== ""
  }

  isAnyModalOpen() {
    const frames = ["auth_modal", "gun_modal"]
    return frames.some(id => {
      const frame = document.getElementById(id)
      return frame && frame.innerHTML.trim() !== ""
    })
  }

  updateScrollLock() {
    if (this.isAnyModalOpen()) {
      this.lockBodyScroll()
    } else {
      this.unlockBodyScroll()
    }
  }

  // ==========================================================================
  // Frame Load Handler
  // ==========================================================================

  frameLoaded() {
    this.updateScrollLock()
  }

  // ==========================================================================
  // Close Actions
  // ==========================================================================

  close(event) {
    event?.preventDefault()
    event?.stopPropagation()

    const frame = this.element
    if (frame && frame.innerHTML.trim() !== "") {
      frame.innerHTML = ""
      this.updateScrollLock()
    }
  }

  closeOnBackdrop(event) {
    // Close on backdrop overlay click
    if (event.target.classList.contains("modal-backdrop-custom") ||
        event.target.classList.contains("modal-overlay")) {
      this.close(event)
      return
    }

    // Close on inset-0 wrapper click (the positioning container)
    if (event.target.classList.contains("inset-0") && event.target === event.currentTarget) {
      this.close(event)
      return
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  copyLink(event) {
    const btn = event.currentTarget
    const url = btn.dataset.modalCopyUrl
    const label = btn.querySelector("span")

    navigator.clipboard.writeText(url).then(() => {
      const original = label.textContent
      label.textContent = "Zkopírováno!"
      setTimeout(() => { label.textContent = original }, 2000)
    })
  }

  handlePopstate() {
    const frame = this.element
    if (frame && frame.innerHTML.trim() !== "") {
      frame.innerHTML = ""
      this.updateScrollLock()
    }
  }
}
